"use client";

import ArticleCoverHeader from "@/components/ArticleCoverHeader";
import ArticleRichText from "@/components/ArticleRichText";
import ArticleSidebar from "@/components/ArticleSidebar";
import BackToBlogButton from "@/components/BackToBlogButton";
import CallToAction from "@/components/CallToAction";
import Page from "@/components/Page";
import Section from "@/components/ui/Section";
import { cn, tinaField, useTina } from "@/lib/utils";
import dayjs from "dayjs";
import advancedFormat from "dayjs/plugin/advancedFormat";
dayjs.extend(advancedFormat);

export default function BlogPostPageClient({ nextPosts = [], ...props }) {
  const { data } = useTina({
    query: props.query,
    variables: props.variables,
    data: props.data,
  });

  const post = data?.post;
  const isPublished = post?.published;
  const { data: blogPageResult } = useTina({
    query: props.blogPageQuery,
    variables: props.blogPageVariables,
    data: props.blogPageData,
  });
  const blogPage = blogPageResult?.blogPage;
  const { data: homeData } = useTina({
    query: props.homeQuery,
    variables: props.homeVariables,
    data: props.homeData,
  });
  const homeCta = homeData?.home?.cta;

  return (
    <Page defaultTinaField={post}>
      <div className="mb-12 space-y-8 max-md:pt-4 lg:mb-24 lg:space-y-12">
        <Section
          wrapperClassName="!overflow-visible md:!pt-8 !pb-0 !mb-0"
          className="grid grid-cols-1 items-start gap-x-8 gap-y-8 lg:grid-cols-[minmax(0,1fr)_280px] lg:gap-y-0 xl:grid-cols-[minmax(0,1fr)_320px]"
        >
          <div className="lg:col-span-2 mb-4">
            <BackToBlogButton />
          </div>

          <article className="min-w-0 overflow-hidden rounded-2xl lg:rounded-3xl">
            <div className="relative overflow-hidden rounded-t-2xl lg:rounded-t-3xl">
              <ArticleCoverHeader
                as="h1"
                size="lg"
                title={post?.title}
                tinaField={tinaField(post, "title")}
                className={cn(isPublished ? "opacity-100" : "opacity-60")}
              />
              {!isPublished && (
                <div className="absolute inset-0 z-10 flex items-center justify-center">
                  <div className="-rotate-45 translate-y-8 text-7xl font-bold text-primary">DRAFT</div>
                </div>
              )}
            </div>
            <div className="rounded-b-2xl border border-border px-4 py-6 sm:px-6 sm:py-8 lg:rounded-b-3xl lg:px-12 lg:py-12">
              <div className="mb-6 flex flex-col gap-2 border-b border-border pb-6 sm:gap-3 lg:flex-row lg:items-start lg:justify-between lg:gap-8">
                {post?.description && (
                  <div data-tina-field={tinaField(post, "description")} className="text-lg min-w-0 flex-1 !font-medium !leading-[150%]">
                    {post.description}
                  </div>
                )}
                {post?.date && (
                  <time dateTime={post.date} className="shrink-0 text-base text-muted-foreground lg:pt-1 lg:text-right">
                    {dayjs(post.date).format("Do MMMM, YYYY")}
                  </time>
                )}
              </div>
              {post?.quickSummary && (
                <div className="mb-6 space-y-2 border-b border-border pb-6">
                  <h2 className="p-lg !font-medium">Quick summary</h2>
                  <p data-tina-field={tinaField(post, "quickSummary")} className="p-lg !leading-[170%]">
                    {post.quickSummary}
                  </p>
                </div>
              )}
              <div data-tina-field={tinaField(post, "body")}>
                <ArticleRichText content={post?.body} />
              </div>
            </div>
          </article>

          <ArticleSidebar nextArticles={nextPosts} tinaFieldData={blogPage} sidebarCta={blogPage?.sidebarCta} />
        </Section>

        {homeCta && <CallToAction tinaFieldData={homeData.home.cta} {...homeCta} />}
      </div>
    </Page>
  );
}
